import React from 'react'
import styled, { css } from 'styled-components'

import type { ClassNameProps } from '@root/shared/react'
import type { Intent } from '@root/shared/ui-kit'

type EditWalletPanelItemProps = {
  icon?: React.ReactNode
  title: React.ReactNode
  onClick?: () => void
  endContent?: React.ReactNode
  intent?: Intent
  notInteractive?: boolean
} & ClassNameProps

export const EditWalletPanelItem: React.FC<EditWalletPanelItemProps> = ({
  icon,
  title,
  onClick,
  endContent,
  intent = 'neutral',
  className,
  notInteractive = false,
}) => {
  const renderIcon = () => {
    if (icon) {
      return <Icon>{icon}</Icon>
    }
  }

  return (
    <Root
      onClick={onClick}
      $hasIcon={!!icon}
      className={className}
      aria-disabled={notInteractive}
      css={css`
        ${(() => {
          switch (intent) {
            case 'error': {
              return css`
                --item-color: var(--error-8);
                --item-color-hover: var(--error-9);
              `
            }
            case 'success': {
              return css`
                --item-color: var(--success-8);
                --item-color-hover: var(--success-9);
              `
            }
            case 'warning': {
              return css`
                --item-color: var(--warning-9);
                --item-color-hover: var(--warning-10);
              `
            }
            case 'neutral': {
              return css`
                --item-color: var(--text-11);
                --item-color-hover: var(--text-12);
              `
            }
          }
        })()}
      `}
    >
      {renderIcon()}
      <Title>{title}</Title>
      <EndContent>{endContent}</EndContent>
    </Root>
  )
}

const Root = styled.div<{ $hasIcon?: boolean }>`
  justify-items: ${({ $hasIcon }) => ($hasIcon ? 'start' : 'end')};
  background: var(--backgroundA-2);

  cursor: pointer;

  border-radius: 8px;
  height: 40px;
  padding-inline: 12px 8px;
  gap: 8px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  transition: all 0.15s linear;

  > :nth-child(1) {
    justify-self: center;
  }
  &:last-of-type {
    border-bottom: 0;
  }
  &:not([aria-disabled='true']):hover {
    background: var(--background-3);
  }
  &[aria-disabled='true'] {
    cursor: default;
  }
`

const Title = styled.div`
  color: var(--item-color);

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;

  transition: all 0.15s linear;

  ${Root}:not([aria-disabled='true']):hover & {
    color: var(--item-color-hover);
  }
`

const Icon = styled.div`
  color: var(--item-color);

  display: grid;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  transition: all 0.15s linear;

  ${Root}:not([aria-disabled='true']):hover & {
    color: var(--item-color-hover);
  }
`

const EndContent = styled.div`
  transition: all 0.15s linear;
  width: 100%;

  ${Root}:not([aria-disabled='true']):hover & {
    > svg {
      color: var(--item-color-hover);
    }
  }
`
